import io from 'socket.io-client';
import { getToken } from './authService';

const SOCKET_URL =
  process.env.REACT_APP_SOCKET_URL ||
  process.env.REACT_APP_API_BASE_URL?.replace('/api', '') ||
  'http://localhost:3001';

let socket = null;

export const getUserIdFromToken = (token) => {
  if (!token) return null;
  try {
    const base64Url = token.split('.')[1];
    const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
    const jsonPayload = decodeURIComponent(
      atob(base64)
        .split('')
        .map((c) => '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2))
        .join('')
    );
    const payload = JSON.parse(jsonPayload);
    return payload.id || payload.sub || payload.userId;
  } catch (error) {
    console.error('Failed to extract userId from token:', error);
    return null;
  }
};

export const getCurrentUserId = () => getUserIdFromToken(getToken());

export const initializeSocket = () => {
  if (socket && socket.connected) {
    return socket;
  }

  const token = getToken();
  const userId = getUserIdFromToken(token);

  socket = io(SOCKET_URL, {
    auth: {
      token: `Bearer ${token}`
    },
    // transports: ['polling'], // force polling — WebSocket upgrade blocked by server (HTTP 400)
    reconnection: true,
    reconnectionDelay: 1000,
    reconnectionDelayMax: 5000,
    reconnectionAttempts: 5,
    query: userId ? { userId: String(userId) } : {}
  });

  socket.on('connect', () => {
    console.log('Socket connected:', socket.id);
    if (userId) {
      socket.emit('join_notification_room', { userId: String(userId) });
    }
  });

  socket.on('disconnect', () => {
    console.log('Socket disconnected');
  });

  socket.on('error', (error) => {
    console.error('Socket error:', error);
  });

  return socket;
};

export const getSocket = () => {
  if (!socket || !socket.connected) {
    return initializeSocket();
  }
  return socket;
};

export const disconnectSocket = () => {
  if (socket) {
    socket.disconnect();
    socket = null;
  }
};

export const onJoinedRoom = (callback, socketInstance) => {
  const socket = socketInstance || getSocket();
  socket.on('joined_room', callback);
  return () => socket.off('joined_room', callback);
};

export const onNotificationCount = (callback, socketInstance) => {
  const socket = socketInstance || getSocket();
  const handleCount = (counts) => {
    console.log('Socket: Received notification count event:', counts);
    callback(counts);
  };
  console.log('Attaching listener for notification-count on socket:', socket.id);
  socket.on('notification-count', handleCount);
  socket.on('notification_count', handleCount);
  return () => {
    socket.off('notification-count', handleCount);
    socket.off('notification_count', handleCount);
  };
};

export const onNotificationsResponse = (callback, socketInstance) => {
  const socket = socketInstance || getSocket();
  socket.on('notifications-response', callback);
  return () => socket.off('notifications-response', callback);
};

export const onNewNotification = (callback, socketInstance) => {
  const socket = socketInstance || getSocket();
  socket.on('new-notification', callback);
  return () => socket.off('new-notification', callback);
};

export const onNotificationViewed = (callback) => {
  const socketInstance = getSocket();
  socketInstance.on('notification-viewed-response', callback);
  return () => socketInstance.off('notification-viewed-response', callback);
};

export const onNotificationRead = (callback) => {
  const socketInstance = getSocket();
  socketInstance.on('notification-read-response', callback);
  return () => socketInstance.off('notification-read-response', callback);
};

export const onNotificationReadAll = (callback) => {
  const socketInstance = getSocket();
  socketInstance.on('notification-read-all-response', callback);
  return () => socketInstance.off('notification-read-all-response', callback);
};

export const fetchNotificationsSocket = (limit = 10, offset = 0) => {
  const socketInstance = getSocket();
  const userId = getCurrentUserId();
  socketInstance.emit('notifications', { limit, offset, userId });
};

export const markNotificationAsReadSocket = (notificationId) => {
  const socketInstance = getSocket();
  const userId = getCurrentUserId();
  socketInstance.emit('notification-read', { userId, notificationId });
};

export const markNotificationAsViewedSocket = () => {
  const socketInstance = getSocket();
  const userId = getCurrentUserId();
  socketInstance.emit('notification-viewed', { userId });
};

export const markAllNotificationsAsReadSocket = () => {
  const socketInstance = getSocket();
  const userId = getCurrentUserId();
  socketInstance.emit('notification-read-all', { userId });
};

export const onBulkFlightSyncProgress = (callback, socketInstance) => {
  const socket = socketInstance || getSocket();
  socket.on('bulk_flight_sync_progress', callback);
  return () => socket.off('bulk_flight_sync_progress', callback);
};
